/**
 * A selectable row: checkbox + leading visual + title/subtitle.
 *
 * Shared by the wizard's meeting-recorder list (StackStep) and suggested-
 * teammate list (InviteStep). The two are structurally identical and differ
 * only in the leading visual, so they compose this single implementation
 * rather than each re-spelling the same layout — see FRONTEND_PATTERNS.md
 * ("Two components that should look the same must share an actual component
 * implementation, not just the same Tailwind classes").
 *
 * The row owns its padding; callers supply the surrounding shell (a bordered
 * card, or a `ListCard` that divides its children).
 */
import type { ReactNode } from 'react';
import { memo, useCallback } from 'react';

import { Checkbox } from '@/lib/base/forms/checkbox';

export interface SelectableRowProps {
  /** Stable id handed back to `onToggle`. */
  id: string;
  /** Leading visual — a brand icon, an `Avatar`, … */
  leading: ReactNode;
  title: string;
  subtitle: string;
  isSelected: boolean;
  onToggle: (id: string) => void;
}

function SelectableRowImpl({
  id,
  leading,
  title,
  subtitle,
  isSelected,
  onToggle,
}: SelectableRowProps) {
  const handleChange = useCallback(() => onToggle(id), [onToggle, id]);

  return (
    <div className='p-4'>
      <Checkbox
        size='md'
        isSelected={isSelected}
        onChange={handleChange}
        label={
          <span className='flex items-center gap-3'>
            {leading}
            <span className='flex flex-col'>
              <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
                {title}
              </span>
              <span className='text-sm font-normal text-[var(--color-text-tertiary)]'>
                {subtitle}
              </span>
            </span>
          </span>
        }
      />
    </div>
  );
}

export const SelectableRow = memo(SelectableRowImpl);
